import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getConnection } from "@/lib/integrations.functions";
import {
  cartroverTestConnection,
  listCartroverMerchants,
  upsertCartroverMerchant,
  deleteCartroverMerchant,
  listCartroverSkuMappings,
  upsertCartroverSkuMapping,
  deleteCartroverSkuMapping,
  listCartroverImports,
  enqueueCartroverMerchantSync,
  getCartroverSettings,
  updateCartroverSettings,
  listCartroverRuns,
} from "@/lib/cartrover/cartrover.functions";
import { CartroverMerchantRulesDialog } from "@/components/admin/cartrover-merchant-rules-dialog";

export const Route = createFileRoute(
  "/_authenticated/app/admin/integrations/cartrover/$connectionId",
)({
  component: CartroverConnectionPage,
});

function CartroverConnectionPage() {
  const { connectionId } = Route.useParams();
  const get = useServerFn(getConnection);
  const { data: connData } = useQuery({
    queryKey: ["cartrover-conn", connectionId],
    queryFn: () => get({ data: { connectionId } }),
  });
  const conn = (connData?.connection ?? null) as
    | { id: string; provider: string; label: string; env: string; status: string }
    | null;

  if (!conn) return <div className="p-4 sm:p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/app/admin/integrations"
            className="text-xs text-muted-foreground hover:underline"
          >
            ← Integrations
          </Link>
          <h2 className="text-xl font-semibold">
            {conn.label}{" "}
            <span className="ml-2 text-xs uppercase text-muted-foreground">
              CartRover · {conn.env}
            </span>
          </h2>
        </div>
        <Badge
          variant={
            conn.status === "active"
              ? "default"
              : conn.status === "error"
              ? "destructive"
              : "secondary"
          }
        >
          {conn.status}
        </Badge>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="merchants">Merchants</TabsTrigger>
          <TabsTrigger value="skus">SKU Mapping</TabsTrigger>
          <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <SettingsTab connectionId={connectionId} />
        </TabsContent>
        <TabsContent value="merchants">
          <MerchantsTab connectionId={connectionId} />
        </TabsContent>
        <TabsContent value="skus">
          <SkusTab connectionId={connectionId} />
        </TabsContent>
        <TabsContent value="exceptions">
          <ExceptionsTab connectionId={connectionId} />
        </TabsContent>
        <TabsContent value="runs">
          <RunsTab connectionId={connectionId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------- Settings -------------------- */

function SettingsTab({ connectionId }: { connectionId: string }) {
  const get = useServerFn(getCartroverSettings);
  const save = useServerFn(updateCartroverSettings);
  const test = useServerFn(cartroverTestConnection);
  const { data, refetch } = useQuery({
    queryKey: ["cartrover-settings", connectionId],
    queryFn: () => get({ data: { connectionId } }),
  });

  const s = (data?.settings ?? {}) as Record<string, string>;
  const [env, setEnv] = useState<string>(s.environment ?? "production");
  const [baseUrl, setBaseUrl] = useState(s.base_url ?? "");
  const [sandboxBaseUrl, setSandboxBaseUrl] = useState(s.sandbox_base_url ?? "");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">REST Endpoint</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Environment</Label>
            <Select value={env} onValueChange={setEnv}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="production">Production (api.cartrover.com)</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="custom">Custom URL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {env === "custom" && (
            <div className="space-y-1">
              <Label>Custom base URL</Label>
              <Input
                placeholder="https://your.cartrover.host"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>
          )}
          {env === "sandbox" && (
            <div className="space-y-1">
              <Label>Sandbox base URL</Label>
              <Input
                placeholder="https://sandbox.cartrover.example"
                value={sandboxBaseUrl}
                onChange={(e) => setSandboxBaseUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                CartRover's sandbox host is not yet confirmed — set this once you
                receive sandbox credentials.
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={async () => {
              await save({
                data: {
                  connectionId,
                  environment: env as "production" | "sandbox" | "custom",
                  baseUrl: baseUrl || undefined,
                  sandboxBaseUrl: sandboxBaseUrl || undefined,
                },
              });
              toast.success("Settings saved");
              refetch();
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const r = await test({ data: { connectionId, merchantId: null } });
              toast[r.ok ? "success" : "error"](r.message);
            }}
          >
            Test connection
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------- Merchants -------------------- */

type MerchantRow = {
  id: string;
  customer_id: string;
  cartrover_merchant_id: string;
  merchant_name: string;
  display_name: string | null;
  credential_mode: "parent" | "merchant";
  active: boolean;
  order_import_enabled: boolean;
  inventory_sync_enabled: boolean;
  shipment_sync_enabled: boolean;
};

function MerchantsTab({ connectionId }: { connectionId: string }) {
  const list = useServerFn(listCartroverMerchants);
  const upsert = useServerFn(upsertCartroverMerchant);
  const del = useServerFn(deleteCartroverMerchant);
  const enqueue = useServerFn(enqueueCartroverMerchantSync);
  const test = useServerFn(cartroverTestConnection);

  const [rulesFor, setRulesFor] = useState<{ id: string; name: string } | null>(
    null,
  );

  const { data, refetch } = useQuery({
    queryKey: ["cartrover-merchants", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const merchants = (data?.merchants ?? []) as MerchantRow[];

  const [draft, setDraft] = useState({
    customerId: "",
    cartroverMerchantId: "",
    merchantName: "",
    credentialMode: "parent" as "parent" | "merchant",
    apiUser: "",
    apiKey: "",
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Merchant</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <Input
            placeholder="Customer ID (UUID)"
            className="sm:col-span-2"
            value={draft.customerId}
            onChange={(e) => setDraft({ ...draft, customerId: e.target.value })}
          />
          <Input
            placeholder="CartRover merchant ID"
            value={draft.cartroverMerchantId}
            onChange={(e) =>
              setDraft({ ...draft, cartroverMerchantId: e.target.value })
            }
          />
          <Input
            placeholder="Merchant name"
            value={draft.merchantName}
            onChange={(e) => setDraft({ ...draft, merchantName: e.target.value })}
          />
          <Select
            value={draft.credentialMode}
            onValueChange={(v) =>
              setDraft({ ...draft, credentialMode: v as "parent" | "merchant" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="parent">Parent creds</SelectItem>
              <SelectItem value="merchant">Merchant creds</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={async () => {
              try {
                await upsert({
                  data: {
                    connectionId,
                    customerId: draft.customerId,
                    cartroverMerchantId: draft.cartroverMerchantId,
                    merchantName: draft.merchantName,
                    credentialMode: draft.credentialMode,
                    merchantCredentials:
                      draft.credentialMode === "merchant"
                        ? { api_user: draft.apiUser, api_key: draft.apiKey }
                        : undefined,
                    orderImportEnabled: true,
                    inventorySyncEnabled: true,
                    shipmentSyncEnabled: true,
                    inventoryCalcMode: "on_hand_minus_allocated",
                    active: true,
                  },
                });
                toast.success("Merchant saved");
                setDraft({
                  customerId: "",
                  cartroverMerchantId: "",
                  merchantName: "",
                  credentialMode: "parent",
                  apiUser: "",
                  apiKey: "",
                });
                refetch();
              } catch (e) {
                toast.error((e as Error).message);
              }
            }}
          >
            Save
          </Button>
          {draft.credentialMode === "merchant" && (
            <>
              <Input
                placeholder="API User"
                className="sm:col-span-3"
                value={draft.apiUser}
                onChange={(e) => setDraft({ ...draft, apiUser: e.target.value })}
              />
              <Input
                type="password"
                placeholder="API Key"
                className="sm:col-span-3"
                value={draft.apiKey}
                onChange={(e) => setDraft({ ...draft, apiKey: e.target.value })}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configured Merchants</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>CR Merchant ID</TableHead>
                <TableHead>Creds</TableHead>
                <TableHead>Sync</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {merchants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground">
                    No merchants linked yet.
                  </TableCell>
                </TableRow>
              )}
              {merchants.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">
                    {m.display_name || m.merchant_name}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {m.cartrover_merchant_id}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{m.credential_mode}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {[
                      m.order_import_enabled && "Orders",
                      m.inventory_sync_enabled && "Inv",
                      m.shipment_sync_enabled && "Ship",
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const r = await test({
                          data: { connectionId, merchantId: m.id },
                        });
                        toast[r.ok ? "success" : "error"](r.message);
                      }}
                    >
                      Test
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setRulesFor({
                          id: m.id,
                          name: m.display_name || m.merchant_name,
                        })
                      }
                    >
                      Rules
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await enqueue({
                          data: {
                            merchantId: m.id,
                            kind: "cartrover_sync_orders_merchant",
                          },
                        });
                        toast.success("Order sync queued");
                      }}
                    >
                      Sync orders
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await enqueue({
                          data: {
                            merchantId: m.id,
                            kind: "cartrover_sync_inventory_merchant",
                          },
                        });
                        toast.success("Inventory sync queued");
                      }}
                    >
                      Sync inv
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        if (!confirm(`Delete merchant ${m.merchant_name}?`)) return;
                        await del({ data: { id: m.id } });
                        toast.success("Deleted");
                        refetch();
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {rulesFor && (
        <CartroverMerchantRulesDialog
          open={!!rulesFor}
          onOpenChange={(v) => !v && setRulesFor(null)}
          merchantId={rulesFor.id}
          merchantName={rulesFor.name}
        />
      )}
    </div>
  );
}

/* -------------------- SKU mappings -------------------- */

function SkusTab({ connectionId }: { connectionId: string }) {
  const listMerchants = useServerFn(listCartroverMerchants);
  const { data: mData } = useQuery({
    queryKey: ["cartrover-merchants-skus", connectionId],
    queryFn: () => listMerchants({ data: { connectionId } }),
  });
  const merchants = (mData?.merchants ?? []) as MerchantRow[];
  const [selected, setSelected] = useState<string>("");

  const list = useServerFn(listCartroverSkuMappings);
  const upsert = useServerFn(upsertCartroverSkuMapping);
  const del = useServerFn(deleteCartroverSkuMapping);
  const { data: skuData, refetch } = useQuery({
    queryKey: ["cartrover-skus", selected],
    queryFn: () => list({ data: { merchantId: selected } }),
    enabled: !!selected,
  });

  const [draft, setDraft] = useState({ cartroverSku: "", skuId: "" });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">SKU Mappings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Select merchant" />
          </SelectTrigger>
          <SelectContent>
            {merchants.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.display_name || m.merchant_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selected && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input
                placeholder="CartRover SKU"
                value={draft.cartroverSku}
                onChange={(e) =>
                  setDraft({ ...draft, cartroverSku: e.target.value })
                }
              />
              <Input
                placeholder="Internal SKU ID (UUID)"
                value={draft.skuId}
                onChange={(e) => setDraft({ ...draft, skuId: e.target.value })}
              />
              <Button
                size="sm"
                onClick={async () => {
                  await upsert({
                    data: {
                      merchantId: selected,
                      cartroverSku: draft.cartroverSku,
                      skuId: draft.skuId,
                      active: true,
                    },
                  });
                  setDraft({ cartroverSku: "", skuId: "" });
                  refetch();
                }}
              >
                Add mapping
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CartRover SKU</TableHead>
                  <TableHead>Internal SKU</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(skuData?.mappings ?? []).map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-mono text-xs">
                      {m.cartrover_sku}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{m.sku_id}</TableCell>
                    <TableCell>{m.active ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={async () => {
                          await del({ data: { id: m.id } });
                          refetch();
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------- Exceptions -------------------- */

function ExceptionsTab({ connectionId }: { connectionId: string }) {
  const list = useServerFn(listCartroverImports);
  const { data } = useQuery({
    queryKey: ["cartrover-exceptions", connectionId],
    queryFn: () =>
      list({ data: { connectionId, status: "exception", limit: 200 } }),
  });
  const rows = data?.imports ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Order Import Exceptions</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CR Order</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Retries</TableHead>
              <TableHead>When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  No exceptions.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">
                  {r.cartrover_order_id}
                </TableCell>
                <TableCell>{r.source_cart ?? "—"}</TableCell>
                <TableCell className="text-xs">
                  <span className="font-medium">{r.exception_code ?? "error"}</span>
                  <span className="text-muted-foreground"> — {r.exception_reason ?? ""}</span>
                </TableCell>
                <TableCell>{r.retry_count}</TableCell>
                <TableCell className="text-xs">
                  {new Date(r.imported_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* -------------------- Runs / history -------------------- */

function RunsTab({ connectionId }: { connectionId: string }) {
  const list = useServerFn(listCartroverRuns);
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["cartrover-runs", connectionId],
    queryFn: () => list({ data: { connectionId, limit: 75 } }),
    refetchInterval: 15_000,
  });
  const events = data?.events ?? [];
  const jobs = data?.jobs ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent Jobs</CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">
                    No jobs.
                  </TableCell>
                </TableRow>
              )}
              {jobs.map((j) => (
                <TableRow key={j.id}>
                  <TableCell className="font-mono text-xs">{j.kind}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        j.status === "succeeded"
                          ? "default"
                          : j.status === "dead" || j.status === "failed"
                          ? "destructive"
                          : "secondary"
                      }
                    >
                      {j.status}
                    </Badge>
                    {j.error && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {j.error}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {j.attempts}/{j.max_attempts}
                  </TableCell>
                  <TableCell className="text-xs">
                    {new Date(j.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dir</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>ms</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground">
                    No activity yet.
                  </TableCell>
                </TableRow>
              )}
              {events.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs uppercase">{e.direction}</TableCell>
                  <TableCell className="font-mono text-xs">{e.kind}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        e.error
                          ? "destructive"
                          : (e.http_status ?? 0) >= 400
                          ? "destructive"
                          : (e.http_status ?? 0) >= 200
                          ? "default"
                          : "secondary"
                      }
                    >
                      {e.http_status ?? (e.error ? "err" : "—")}
                    </Badge>
                    {e.error && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {e.error}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{e.duration_ms ?? "—"}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(e.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}